/**
 * AGC: Simulation Battle Component - 100% Backend Battle Simulation
 * AGC: Includes difficulty and NFT selection like QuickBattle
 * AGC: No blockchain interaction, pure backend simulation
 */

import React, { useState } from 'react'
import { Socket } from 'socket.io-client'
import { usePlayerProgression } from '../hooks/usePlayerProgression'
import { HybridPvEBattle } from './HybridPvEBattle'
import { playSoundSafe, unlockAudioPlayback } from '../utils/audio'
import { BuffSelector } from './BuffSelector'
import { PotionSelector } from './PotionSelector'
import { useModal } from '../hooks/useModal'
import { Info } from 'lucide-react'

interface SimulationBattleProps {
  playerAddress: string
  socket: Socket
  onBackToMenu: () => void
}

export const SimulationBattle: React.FC<SimulationBattleProps> = ({
  playerAddress,
  socket,
  onBackToMenu
}) => {
  const { playerStats } = usePlayerProgression(playerAddress)
  const { alert, Modals } = useModal()

  // AGC: Selection state
  const [difficulty, setDifficulty] = useState<'normal' | 'expert' | 'insane'>('normal')
  const [selectedBuffs, setSelectedBuffs] = useState<number[]>([])
  const [selectedPotion, setSelectedPotion] = useState<number | null>(null)
  const [showBattle, setShowBattle] = useState(false)
  const [battleId, setBattleId] = useState<string>('')
  const [botLevel, setBotLevel] = useState<number>(1)
  
  // AGC: Battle result modal state
  const [showResultModal, setShowResultModal] = useState(false)
  const [battleResult, setBattleResult] = useState<{
    won: boolean
    turns: number
    isDraw?: boolean
  } | null>(null)

  // AGC: Difficulty levels
  const difficultyLevels = {
    normal: { level: 3, name: 'Easy', description: 'Easy bot, good for learning' },
    expert: { level: 5, name: 'Expert', description: 'Challenging bot, requires strategy' },
    insane: { level: 15, name: 'Insane', description: 'Extreme difficulty, for masters only' }
  }

  const handleStartBattle = async () => {
    if (selectedBuffs.length === 0) {
      await alert('Please select at least one buff NFT for the simulation!', { type: 'warning' })
      return
    }
    
    // AGC: Generate unique battle ID for simulation
    const newBattleId = `sim-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    unlockAudioPlayback()
    setBattleId(newBattleId)
    setBotLevel(difficultyLevels[difficulty].level)
    setShowBattle(true)
  }

  const handleBattleEnd = (won: boolean, turns: number, _isDraw?: boolean) => {
    console.log(`🎮 Simulation battle ended: ${won ? 'WON' : 'LOST'} in ${turns} turns`)
    
    // AGC: Play win/lose sound like InteractiveP2PBattle
    if (won) {
      playSoundSafe('/sounds/youwin.mp3', 0.6)
    } else {
      playSoundSafe('/sounds/gameover.mp3', 0.6)
    }
    
    // AGC: Show beautiful modal like InteractiveP2PBattle
    setBattleResult({ won, turns, isDraw: _isDraw })
    setShowResultModal(true)
    setShowBattle(false)
  }

  const handleCloseResultModal = () => {
    setShowResultModal(false)
    setBattleResult(null)
    onBackToMenu()
  }

  // AGC: If battle is active, show the battle component
  if (showBattle) {
    return (
      <HybridPvEBattle
        battleId={battleId}
        playerAddress={playerAddress}
        botLevel={botLevel}
        socket={socket}
        selectedBuffs={selectedBuffs}
        onBattleEnd={handleBattleEnd}
        onBackToMenu={() => setShowBattle(false)}
      />
    )
  }

  // AGC: Show selection screen - EXACT copy of QuickBattle's buff-selection phase
  return (
    <div className="bg-slate-800 rounded-lg p-6 max-w-4xl mx-auto">
      {/* AGC: Flashy green info panel at the top */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg p-4 mb-6 flex items-center gap-3 shadow-lg border-2 border-green-400">
        <Info className="w-6 h-6 text-white flex-shrink-0" />
        <p className="text-white font-semibold text-sm md:text-base">
          Test the battle engine without any blockchain interaction, no connection to your wallet
        </p>
      </div>

      {/* AGC: Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">⚡ Simulation Battle Setup</h2>
        <p className="text-slate-400">Select your buffs and difficulty to test the battle engine!</p>
      </div>

      {/* AGC: Player info - EXACT match to QuickBattle */}
      <div className="bg-slate-700 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-bold text-white mb-3">👤 Player Status</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-slate-400 text-sm">Address</p>
            <p className="text-white font-mono text-sm">
              {playerAddress.substring(0, 6)}...{playerAddress.substring(38)}
            </p>
          </div>
          <div>
            <p className="text-slate-400 text-sm">Simulation Mode</p>
            <p className="text-green-400 font-bold">No Cost</p>
          </div>
        </div>
      </div>

      {/* AGC: Difficulty Selection */}
      <div className="bg-slate-700 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-bold text-white mb-4">🎯 Select Difficulty</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(difficultyLevels).map(([key, config]) => (
            <button
              key={key}
              onClick={() => setDifficulty(key as any)}
              className={`p-4 rounded-lg border-2 transition-all ${
                difficulty === key
                  ? 'border-purple-500 bg-purple-500/20 text-white'
                  : 'border-slate-600 bg-slate-600/20 text-gray-300 hover:border-purple-400 hover:bg-purple-400/10'
              }`}
            >
              <h3 className="text-lg font-bold mb-2">{config.name}</h3>
              <p className="text-sm opacity-80">{config.description}</p>
              <p className="text-xs mt-2 text-slate-400">Bot Level: {config.level}</p>
            </button>
          ))}
        </div>
      </div>

      {/* AGC: Buff selector */}
      <div className="mb-6">
        <BuffSelector
          playerAddress={playerAddress}
          onBuffsSelected={setSelectedBuffs}
          maxBuffs={2}
          disabled={false}
        />
      </div>

      {/* AGC: Potion Selection */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white mb-3">⚗️ Select Classic Potion (Optional)</h3>
        <p className="text-slate-400 text-sm mb-3">Choose one potion to use during battle (one-time use)</p>
        <PotionSelector
          playerAddress={playerAddress}
          onPotionSelected={(potionId) => {
            setSelectedPotion(potionId)
            if (potionId !== null) {
              console.log('⚗️ Potion selected:', potionId)
            }
          }}
          disabled={false}
        />
      </div>

      {/* AGC: Action buttons */}
      <div className="flex gap-4">
        <button
          onClick={onBackToMenu}
          className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300"
        >
          ← Back to Menu
        </button>
        <button
          onClick={handleStartBattle}
          disabled={selectedBuffs.length === 0}
          className={`flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 ${
            selectedBuffs.length === 0 ? 'cursor-not-allowed' : ''
          }`}
        >
          ⚡ Start Simulation Battle
        </button>
      </div>

      {/* AGC: Modal components */}
      {Modals}

      {/* AGC: Beautiful Result Modal - Like InteractiveP2PBattle */}
      {showResultModal && battleResult && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-700 rounded-lg p-8 text-center max-w-md w-full">
            <div className="text-6xl mb-4">
              {battleResult.won ? '🏆' : '💀'}
            </div>
            <h3 className="text-2xl font-bold mb-4 text-white">
              {battleResult.won ? 'VICTORY!' : 'DEFEAT'}
            </h3>
            <p className="text-gray-300 mb-8">
              Battle completed in {battleResult.turns} turns!
            </p>
            <div className="mt-auto">
              <button
                onClick={handleCloseResultModal}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold py-3 px-8 rounded-lg transition-all shadow-lg hover:shadow-xl"
              >
                Back to Menu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
